import { useTranslation } from 'react-i18next';
import { HashRouter, Routes, Route, Outlet, useLocation } from 'react-router-dom';

import { Box, Typography } from '@material-ui/core';

import { LogoIcon } from '@admin/shared/components/icons/logo';

import { LoginForm } from '@admin/login/components/login-form/login-form';
import { ForgotPasswordForm } from '@admin/login/components/forgot-password-form/forgot-password-form';
import { ResetPasswordForm } from '@admin/login/components/reset-password-form/reset-password-form';
import { TotpForm } from '@admin/login/components/totp-input';

// constants for login embedded pages
const LOGIN_PAGES = {
  de: 'https://omq.ai/de/admin-ui-login/',
  en: 'https://omq.ai/admin-ui-login/',
};

// routes of module
export const LOGIN_ROUTES = {
  LOGIN: '/',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  TOTP_SETUP: '/totp-setup',
  TOTP_INPUT: '/totp',
};

/**
 * Wrapper for router, each route will be rendered inside outlet.
 *
 * @returns {JSX.Element}
 */
function Root(): JSX.Element {
  const { i18n, t } = useTranslation(['login']);
  const { pathname } = useLocation();

  // get current language & url of website login teaser page
  const uiLanguage = i18n.language.substr(0, 2);
  const frameURL = uiLanguage === 'de' ? LOGIN_PAGES.de : LOGIN_PAGES.en;

  return (
    <Box bgcolor="grey.100" width={1} height={1} display="flex">
      <Box
        display="flex"
        flexDirection="column"
        bgcolor="grey.100"
        height={1}
        width={480}
        flexGrow={0}
        flexShrink={0}
        padding="66px 40px 16px 40px">
        <Box width={100} height={37} flexShrink={0} flexGrow={0} mb="130px" color="primary.main">
          <LogoIcon
            style={{
              width: '100%',
              height: '100%',
            }}
          />
        </Box>

        <Box flexGrow={1}>
          <Outlet />
        </Box>

        <Box height={30} flexGrow={0} flexShrink={0}>
          <Typography variant="body2" color="textSecondary">
            {t('headlines.trademark', {
              date: new Date().getFullYear(),
            })}
          </Typography>
        </Box>
      </Box>

      {pathname !== LOGIN_ROUTES.RESET_PASSWORD && (
        <Box flexGrow={1} overflow="hidden">
          <iframe
            src={frameURL}
            seamless
            style={{
              height: '100%',
              width: '100%',
              border: 'none',
            }}
          />
        </Box>
      )}
    </Box>
  );
}

/**
 * Main component for login module.
 *
 * Renders LoginForm, ForgotPasswordForm or reset password form
 *
 * @author Florian Walch
 * @since 9.7
 *
 * @returns {JSX.Element}
 */
export const LoginModule = (): JSX.Element => {
  return (
    <HashRouter>
      <Routes>
        <Route path="/" element={<Root />}>
          <Route path={LOGIN_ROUTES.LOGIN} element={<LoginForm />} />
          <Route path={LOGIN_ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordForm />} />
          <Route path={LOGIN_ROUTES.RESET_PASSWORD} element={<ResetPasswordForm />} />
          <Route path={LOGIN_ROUTES.TOTP_SETUP} element={<TotpForm setup={true} />} />
          <Route path={LOGIN_ROUTES.TOTP_INPUT} element={<TotpForm />} />
        </Route>
      </Routes>
    </HashRouter>
  );
};
